export interface PreloadImageSourceEntry {
  path: string;
  size?: string;
}

export interface PreloadImageSource {
  srcset?: PreloadImageSourceEntry[] | string;
  width?: number;
  media?: string;
}

export interface PreloadImageData {
  image?: {
    src?: string;
    srcset?: string;
  };
  sources?: PreloadImageSource[];
}

const preloadedSources = new Set<string>();

function formatSourceEntry(entry: PreloadImageSourceEntry, sourceWidth?: number): string {
  if (entry.size || entry.path.includes(' ')) {
    return [entry.path, entry.size].filter(Boolean).join(' ');
  }

  if (sourceWidth) {
    return `${entry.path} ${sourceWidth}w`;
  }

  return entry.path;
}

function createMergedSrcset(sources: PreloadImageSource[] = [], imageSrcset?: string): string {
  const sourceSets = sources.flatMap((source) => {
    if (!source.srcset) {
      return [];
    }

    if (typeof source.srcset === 'string') {
      return source.srcset;
    }

    return source.srcset.map((entry) => formatSourceEntry(entry, source.width)).join(', ');
  });

  if (imageSrcset) {
    sourceSets.push(imageSrcset);
  }

  return sourceSets.filter(Boolean).join(', ');
}

function createMergedSizes(sources: PreloadImageSource[] = []): string {
  return sources
    .map((source) => {
      if (!source.width) {
        return '';
      }

      return source.media ? `${source.media} ${source.width}px` : `${source.width}px`;
    })
    .filter(Boolean)
    .join(', ');
}

function getFallbackFromSrcset(srcset?: PreloadImageSourceEntry[] | string): string | null {
  if (!srcset) {
    return null;
  }

  if (typeof srcset === 'string') {
    return srcset.split(',')[0]?.trim().split(/\s+/)[0] ?? null;
  }

  return srcset[0]?.path.split(',')[0]?.trim().split(/\s+/)[0] ?? null;
}

function getFallbackImage(data: PreloadImageData): string | null {
  if (data.image?.src) {
    return data.image.src;
  }

  for (const source of data.sources ?? []) {
    const sourceFallback = getFallbackFromSrcset(source.srcset);

    if (sourceFallback) {
      return sourceFallback;
    }
  }

  return getFallbackFromSrcset(data.image?.srcset);
}

function preloadImage(data: PreloadImageData): Promise<void> {
  const fallbackSource = getFallbackImage(data);

  if (!fallbackSource || preloadedSources.has(fallbackSource) || typeof Image === 'undefined') {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const srcset = createMergedSrcset(data.sources, data.image?.srcset);
    const sizes = createMergedSizes(data.sources);

    image.onload = () => {
      preloadedSources.add(fallbackSource);
      resolve();
    };

    image.onerror = () => {
      reject(new Error(`Unable to preload image: ${fallbackSource}`));
    };

    if (srcset) {
      image.srcset = srcset;
    }

    if (sizes) {
      image.sizes = sizes;
    }

    image.src = fallbackSource;
  });
}

export function preloadImages(images: PreloadImageData | PreloadImageData[]): Promise<void> {
  const imageList = Array.isArray(images) ? images : [images];

  return Promise.all(imageList.map(preloadImage)).then(() => undefined);
}
